import React, { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

export const SSNInput = (props) => {
  const {
    item,
    showPassword = false,
    value: propsValue = "",
    onChange,
    onBlur,
    currentPiiInfoFields,
    handleFieldLevelHandler,
    disabled = false,
    placeholder = "XXX-XX-XXXX",
    className = "",
    error = { isError: false, message: "" },
    ...restProps
  } = props;

  const [rawSSN, setRawSSN] = useState("");
  const [maskedSsn, setMaskedSsn] = useState("");

  useEffect(() => {
    const fieldValue = propsValue !== null ? propsValue : "";
    setRawSSN(format(fieldValue));
  }, [propsValue]);

  const replaceCharacters = (str) => {
    if (str?.length <= 1) {
      return str;
    }

    const lastCharacter = str.slice(-1);
    const replacedCharacters = str.slice(0, -1).replace(/[^-]/g, "X");
    return replacedCharacters + lastCharacter;
  };

  const maskAndSetData = () => {
    if (!showPassword) setMaskedSsn(rawSSN.replace(/\d/g, "X"));
    else {
      setMaskedSsn(rawSSN);
    }
  };

  useEffect(() => {
    let timer;
    // Replace characters with X after a second of typing
    if (rawSSN?.length > 0) {
      if (rawSSN.length > maskedSsn.length) {
        const replacedStr = replaceCharacters(rawSSN);
        setMaskedSsn(replacedStr);
        timer = setTimeout(() => {
          setMaskedSsn(rawSSN.replace(/\d/g, "X"));
        }, 500);
      } else {
        maskAndSetData();
      }
    } else {
      setMaskedSsn("");
    }

    return () => {
      clearTimeout(timer);
    };
  }, [rawSSN]);

  const handleChange = (e) => {
    const { value } = e.target;
    if (value === "") {
      setRawSSN("");
      setMaskedSsn("");
      if (onChange) onChange(e, "");
      return;
    }
    if (value.length === 1) {
      setRawSSN(format(value.replace(/\D/g, "")));
      return;
    }
    if (value.replace(/-/g, "").length < rawSSN.replace(/-/g, "").length) {
      const last = rawSSN.replace(/-/g, "").length - 1;
      const newValue = format(rawSSN.replace(/-/g, "").slice(0, last));
      setRawSSN(newValue);
      if (onChange) onChange(e, rawSSN.replace(/-/g, "").slice(0, last));
      return;
    }
    if (!showPassword) {
      const numValue = value.replace(/\D/g, "");
      let newSSN = "";

      const temp = 11;
      if (rawSSN.replace(/-/g, "").length > temp) {
        newSSN = rawSSN.replace(/-/g, "").slice(0, temp) + numValue;
      } else {
        newSSN =
          rawSSN.replace(/-/g, "") + numValue.charAt(numValue.length - 1);
      }
      if (newSSN.length > 9) {
        return;
      }
      setRawSSN(format(newSSN));
      if (onChange) onChange(e, newSSN);
    } else {
      setRawSSN(format(value));
      if (onChange) onChange(e, value);
    }
  };

  const format = (v) => {
    if (!v) return v;
    v = v.slice(0, 11).replace(/-/g, "");
    if (v.length <= 3) {
      return v;
    }
    if (v.length > 3 && v.length <= 5) {
      return `${v.slice(0, 3)}-${v.slice(3)}`;
    }
    if (v.length > 5) {
      return `${v.slice(0, 3)}-${v.slice(3, 5)}-${v.slice(5)}`;
    }
  };

  // Helper function to mask SSN (similar to MUI reference maskSSN function)
  const maskSSN = (ssn) => {
    if (!ssn) {
      return "";
    }
    const maskedSSN = ssn.slice(-4);
    return `XXX-XX-${maskedSSN}`;
  };

  const inputValue = !showPassword ? rawSSN : maskSSN(propsValue);
  const isDisabled = disabled || showPassword;

  return (
    <div className="relative">
      <input
        {...restProps}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onBlur={(e) => {
          if (onBlur) onBlur(e, rawSSN.replace(/-/g, ""));
        }}
        autoComplete="off"
        disabled={isDisabled}
        placeholder={placeholder}
        className={`
          w-full px-3 py-2 pr-10 border rounded-md text-sm transition-colors
          ${
            error.isError
              ? "border-red-500 hover:border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          }
          focus:outline-none focus:ring-1 font-mono tracking-wider
          ${className}
        `}
      />

      <button
        type="button"
        onClick={() => {
          return handleFieldLevelHandler(
            item,
            currentPiiInfoFields?.[item.uniqueField]
          );
        }}
        className={`
          absolute right-2 top-1/2 transform -translate-y-1/2
          p-1 rounded-md text-gray-500 hover:text-gray-600
          transition-colors duration-200
        `}
      >
        {!showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
      </button>
    </div>
  );
};
